import { useCallback, useEffect, useState } from "react";
import { Check, Loader2, MailPlus, X } from "lucide-react";
import { toast } from "sonner";
import { api, apiError } from "@/lib/api";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Textarea } from "@/components/ui/textarea";
import { inviteReason, inviteSummary, parseEmails, MAX_INVITES } from "@/lib/inviteReason";

// Invite people to a room by email, then see who got it, who joined and who needs a nudge.
export const RoomInvites = ({ roomId, emailReady }) => {
  const [emails, setEmails] = useState("");
  const [invites, setInvites] = useState([]);
  const [busy, setBusy] = useState(false);
  const count = parseEmails(emails).length;

  const load = useCallback(async () => {
    if (!roomId) return setInvites([]);
    try {
      const { data } = await api.get(`/rooms/${roomId}/invites`);
      setInvites(data);
    } catch (e) {
      console.error("invite history failed", e);
    }
  }, [roomId]);

  useEffect(() => { load(); }, [load]);

  const sendInvite = async () => {
    const targets = parseEmails(emails);
    if (!targets.length) return toast.error("Add at least one valid email address");
    if (targets.length > MAX_INVITES)
      return toast.error(`Up to ${MAX_INVITES} addresses at a time — you pasted ${targets.length}`);
    setBusy(true);
    try {
      const { data } = await api.post(`/rooms/${roomId}/invite`, {
        emails: targets, origin_url: window.location.origin,
      });
      if (data.sent_count) setEmails("");
      if (!data.sent_count) navigator.clipboard?.writeText(data.link);
      toast[data.sent ? "success" : "error"](inviteSummary(data, "join"),
                                            { duration: data.sent ? 5000 : 9000 });
      load();
    } catch (e) {
      toast.error(apiError(e.response?.data?.detail));
    } finally {
      setBusy(false);
    }
  };

  const retry = async (inviteId) => {
    setBusy(true);
    try {
      const { data } = await api.post(`/rooms/${roomId}/invites/retry`, {
        invite_id: inviteId || null, origin_url: window.location.origin,
      });
      toast[data.sent ? "success" : "error"](inviteSummary(data, "join"),
                                            { duration: data.sent ? 5000 : 9000 });
      load();
    } catch (e) {
      toast.error(apiError(e.response?.data?.detail));
    } finally {
      setBusy(false);
    }
  };

  const remind = async (inviteId) => {
    try {
      const { data } = await api.post(`/rooms/${roomId}/invites/remind`, {
        invite_id: inviteId, origin_url: window.location.origin,
      });
      toast[data.sent ? "success" : "error"](
        data.sent ? `MiLu nudged ${data.email}` : inviteReason(data, "reminder"),
        { duration: data.sent ? 5000 : 9000 },
      );
      load();
    } catch (e) {
      toast.error(apiError(e.response?.data?.detail));
    }
  };

  const clear = async (inviteId) => {
    try {
      await api.delete(`/rooms/${roomId}/invites`,
                       { params: inviteId ? { invite_id: inviteId } : {} });
      setInvites((prev) => (inviteId ? prev.filter((iv) => iv.invite_id !== inviteId) : []));
    } catch (e) {
      toast.error(apiError(e.response?.data?.detail));
    }
  };

  const failed = invites.filter((iv) => !iv.sent).length;

  return (
    <>
      {count > 1 ? (
        <Textarea
          data-testid="room-invite-email-input"
          value={emails}
          onChange={(e) => setEmails(e.target.value)}
          rows={3}
          placeholder="Paste emails — commas, spaces or new lines"
          className="min-h-[76px] flex-1 rounded-2xl"
        />
      ) : (
        <Input
          data-testid="room-invite-email-input"
          value={emails}
          onChange={(e) => setEmails(e.target.value)}
          onKeyDown={(e) => e.key === "Enter" && sendInvite()}
          placeholder={emailReady ? "Invite by email — paste a list" : "Invite by email (copies link)"}
          className="h-10 w-56 rounded-full"
        />
      )}
      <Button
        data-testid="room-invite-email-button"
        size="sm"
        variant="outline"
        onClick={sendInvite}
        disabled={busy || !count}
        className="min-h-[40px] rounded-full px-4 active:scale-95"
      >
        {busy ? <Loader2 className="h-3.5 w-3.5 animate-spin" /> : <MailPlus className="h-3.5 w-3.5" />}
        <span className="ms-1.5 hidden sm:inline">
          {busy ? "Sending…" : count > 1 ? `Invite ${count}` : "Invite"}
        </span>
      </Button>

      {invites.length > 0 && (
        <div className="w-full pt-1" data-testid="room-invite-history">
          <ul className="space-y-1">
            {invites.map((iv) => (
              <li key={iv.invite_id} className="group flex items-center gap-2 text-xs text-ink-soft">
                {iv.sent ? <Check className="h-3 w-3 shrink-0 text-forest" />
                         : <X className="h-3 w-3 shrink-0 text-destructive" />}
                <span className="truncate">
                  {iv.sent ? "Invite sent to" : "Not sent to"} <b className="text-ink">{iv.email}</b>
                  {" · "}
                  {new Date(iv.created_at).toLocaleString()}
                </span>
                <span className="ms-auto flex shrink-0 items-center gap-1.5">
                  {!iv.sent && (
                    <button
                      data-testid={`retry-invite-${iv.invite_id}`}
                      onClick={() => retry(iv.invite_id)}
                      disabled={busy}
                      className="rounded-full border border-white/10 px-2 py-0.5 text-[11px] text-ink-soft transition-colors hover:border-brand/50 hover:text-ink"
                    >
                      Retry
                    </button>
                  )}
                  {iv.sent && iv.joined && (
                    <span data-testid={`invite-joined-${iv.invite_id}`}
                          className="text-[11px] font-semibold text-forest">
                      Joined
                    </span>
                  )}
                  {iv.sent && !iv.joined && iv.reminded_at && (
                    <span className="text-[11px] text-ink-soft/70">Reminded</span>
                  )}
                  {iv.sent && !iv.joined && !iv.reminded_at && (
                    <button
                      data-testid={`remind-invite-${iv.invite_id}`}
                      onClick={() => remind(iv.invite_id)}
                      className="rounded-full border border-white/10 px-2 py-0.5 text-[11px] text-ink-soft transition-colors hover:border-brand/50 hover:text-ink"
                    >
                      Remind
                    </button>
                  )}
                  <button
                    data-testid={`dismiss-invite-${iv.invite_id}`}
                    aria-label={`Remove ${iv.email} from the invite history`}
                    onClick={() => clear(iv.invite_id)}
                    className="rounded-full p-1 text-ink-soft/60 transition-colors hover:text-ink"
                  >
                    <X className="h-3 w-3" />
                  </button>
                </span>
              </li>
            ))}
          </ul>
          <div className="mt-1.5 flex flex-wrap items-center gap-3">
            {failed > 1 && (
              <button
                data-testid="retry-all-invites"
                onClick={() => retry()}
                disabled={busy}
                className="text-xs font-semibold text-brand underline decoration-dotted hover:text-brand-hover"
              >
                Retry all failed ({failed})
              </button>
            )}
            <button
              data-testid="clear-invite-history"
              onClick={() => clear()}
              className="text-xs text-ink-soft underline decoration-dotted hover:text-ink"
            >
              Clear invite history
            </button>
          </div>
        </div>
      )}
    </>
  );
};
